import { useParams, useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, BookOpen } from "lucide-react";
import { airports } from "@/data/airports";
import { useSavedAirports } from "@/hooks/useSavedAirports";
import { SaveAirportButton } from "@/components/airport/SaveAirportButton";
import { useMultiSourceWaitTime } from "@/hooks/useMultiSourceWaitTime";
import { Helmet } from "react-helmet-async";
import { getAirportSnippet } from "@/data/airportSnippets";
import {
  SchemaMarkup,
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateAirportSchema,
  generateHowToSchema,
} from "@/components/SchemaMarkup";
import { RelatedContent } from "@/components/RelatedContent";
import { AirportFAQ } from "@/components/AirportFAQ";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedAirports } from "@/components/RelatedAirports";
import { WaitTimeHero } from "@/components/airport/WaitTimeHero";
import { AirportTabs } from "@/components/airport/AirportTabs";
import { FlightPreview } from "@/components/airport/FlightPreview";
import { RestaurantPreview } from "@/components/airport/RestaurantPreview";
import { SmartSignupBanner } from "@/components/airport/SmartSignupBanner";
import { CrawlableContent } from "@/components/airport/CrawlableContent";
import { BlogCrossLinks } from "@/components/airport/BlogCrossLinks";
import { BestTimesToFly } from "@/components/airport/BestTimesToFly";
import { PopularAirports } from "@/components/airport/PopularAirports";
import { EmailCapture } from "@/components/EmailCapture";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import { trackCtaClick } from "@/data/appLinks";
import { trackEvent } from "@/lib/productEvents";
import { QuickAirportRow } from "@/components/airport/QuickAirportRow";
import { AppInstallBanner } from "@/components/AppInstallBanner";
import { ProUpsellStrip } from "@/components/ProUpsellStrip";
import { getTerminalInfo } from "@/data/airlineTerminals";
import { getDiningInfo } from "@/data/airportDining";
import { useState } from "react";
import NotFound from "./NotFound";

const AirportDetail = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const airport = airports.find((a) => a.code.toLowerCase() === code?.toLowerCase());
  const { waitTime, loading, error } = useMultiSourceWaitTime(code?.toUpperCase());
  const { isSaved, toggleSave } = useSavedAirports();
  const [installBannerVisible, setInstallBannerVisible] = useState(false);

  // Record that a visitor actually saw a wait time for this airport.
  useEffect(() => {
    if (airport && !loading && waitTime) {
      trackEvent("wait_view", airport.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [airport?.code, loading, Boolean(waitTime)]);

  if (!airport) return <NotFound />;

  const hasTerminalData = Boolean(getTerminalInfo(airport.code));
  const hasDiningData = Boolean(getDiningInfo(airport.code));
  const cityName = airport.city.split(",")[0];
  const stateName = airport.city.split(",")[1]?.trim() || "";

  const faqData = [
    {
      question: `What is the average TSA wait time at ${airport.code}?`,
      answer: `TSA wait times at ${airport.name} vary throughout the day. Typical wait times range from 5-15 minutes during off-peak hours and 15-45 minutes during peak travel times (5-8 AM and 4-7 PM). Use Airport Wait Buddy to check real-time wait times before you travel.`,
    },
    {
      question: `How early should I arrive at ${airport.code}?`,
      answer: `For domestic flights from ${airport.name}, arrive at least 2 hours before departure. For international flights, arrive 3 hours early. During peak travel seasons (holidays, summer), add an extra 30 minutes. Check current TSA wait times on Airport Wait Buddy to plan accordingly.`,
    },
    {
      question: `Does ${airport.code} have TSA PreCheck?`,
      answer: `Yes, ${airport.name} offers TSA PreCheck lanes. TSA PreCheck members typically experience wait times of 5 minutes or less. You can check PreCheck availability and current wait times in real-time on Airport Wait Buddy.`,
    },
    {
      question: `What are the busiest times at ${airport.code} security?`,
      answer: `${airport.name} experiences the highest security wait times during early morning (5-8 AM) and evening (4-7 PM) hours on weekdays. Weekend mornings and holiday travel periods are also busy. Use our real-time data to avoid peak congestion.`,
    },
    {
      question: `Can I track my flight at ${airport.code}?`,
      answer: `Yes! Airport Wait Buddy provides real-time flight tracking for ${airport.name}. Sign in to view departure times, gate information, flight status, and delays for all airlines operating at ${airport.code}.`,
    },
    {
      question: `Where can I eat at ${airport.code}?`,
      answer: `${airport.name} has numerous dining options across all terminals. Airport Wait Buddy shows you available restaurants at ${airport.code} with locations, hours, and menu types to help you plan your pre-flight meal.`,
    },
  ];

  const howToSteps = [
    { name: "Check Current Wait Time", text: `View the live TSA security wait time at ${airport.name}` },
    { name: "Plan Your Arrival", text: "Calculate when to leave based on current conditions" },
    { name: "Monitor Your Flight", text: "Check for delays or gate changes before departing" },
    { name: "Arrive Stress-Free", text: "Get to your gate with time to spare" },
  ];

  const currentDate = new Date().toISOString().split("T")[0];

  // Optional per-airport snippet override (leaves default logic untouched elsewhere)
  const snippet = getAirportSnippet(airport.code);
  const waitMinutes = waitTime ? String(waitTime.standard.waitTime) : "";
  const defaultTitle = `${airport.code} TSA Wait Time NOW – ${waitTime ? `${waitMinutes} Min` : "Live"} | ${cityName}`;
  const defaultDescription = waitTime
    ? `${airport.code} TSA wait time is ${waitMinutes} minutes right now at ${airport.name}, ${cityName}. Live security and PreCheck lines by terminal, updated every 5 minutes.`
    : `Check the security line at ${airport.code} before you leave. Live ${airport.name} TSA and PreCheck waits by terminal in ${cityName}, updated every 5 minutes.`;
  const pageTitle = snippet
    ? (waitTime ? snippet.titleLive.replace("{wait}", waitMinutes) : snippet.titleFallback)
    : defaultTitle;
  const pageDescription = snippet
    ? (waitTime ? snippet.descriptionLive.replace("{wait}", waitMinutes) : snippet.descriptionFallback)
    : defaultDescription;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${airport.code} TSA wait time now, ${airport.code} TSA wait time right now, how long is TSA at ${airport.code} today, ${airport.name} security, ${cityName} airport TSA today, ${airport.code} PreCheck wait time`} />
        <link rel="canonical" href={`https://airportbuddy.app/airports/${code?.toLowerCase()}`} />
        <meta name="robots" content="index, follow" />
        <meta property="article:modified_time" content={currentDate} />
        <meta property="og:updated_time" content={currentDate} />
        <meta property="og:title" content={`${airport.code} TSA: ${waitTime ? `${waitTime.standard.waitTime} Min Right Now` : "Check Live"}`} />
        <meta property="og:description" content={`Current TSA wait at ${airport.name}. Live data updated every 5 minutes.`} />
        <meta property="og:url" content={`https://airportbuddy.app/airports/${code?.toLowerCase()}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${airport.code} TSA: ${waitTime ? `${waitTime.standard.waitTime} Min` : "Live Times"}`} />
        <meta name="twitter:description" content={`Current TSA wait time at ${airport.name}. Check before you leave.`} />
      </Helmet>

      <SchemaMarkup schema={generateAirportSchema(airport.name, airport.code, cityName, stateName)} />
      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Airports", url: "/" },
        { name: `${airport.code} - ${cityName}`, url: `/airports/${code?.toLowerCase()}` },
      ])} />
      <SchemaMarkup schema={generateFAQSchema(faqData)} />
      {/* TravelAction schema */}
      <SchemaMarkup schema={{
        "@context": "https://schema.org",
        "@type": "TravelAction",
        "name": `Check TSA Wait Times at ${airport.name}`,
        "description": `View real-time TSA security checkpoint wait times at ${airport.name} (${airport.code})`,
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `https://airportbuddy.app/airports/${code?.toLowerCase()}`
        },
        "location": {
          "@type": "Airport",
          "name": airport.name,
          "iataCode": airport.code
        }
      }} />

      {/* WebPage schema */}
      <SchemaMarkup schema={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": `${airport.name} (${airport.code}) TSA Wait Times`,
        "description": `Live TSA security wait times at ${airport.name} (${airport.code}). Check real-time checkpoint lines, best times to arrive, and terminal tips. Updated every 5 minutes.`,
        "url": `https://airportbuddy.app/airports/${code?.toLowerCase()}`,
        "dateModified": currentDate,
        "isPartOf": {
          "@type": "WebSite",
          "name": "AirportBuddy",
          "url": "https://airportbuddy.app"
        }
      }} />
      <SchemaMarkup schema={generateHowToSchema(
        `How to Check TSA Wait Time at ${airport.code}`,
        `Step-by-step guide to checking real-time TSA security wait times at ${airport.name}`,
        howToSteps,
        "PT1M"
      )} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-8">
          <Breadcrumbs items={[
            { label: "Home", href: "/" },
            { label: "Airports", href: "/airports" },
            { label: `${airport.code} - ${cityName}`, href: `/airports/${code?.toLowerCase()}` },
          ]} />

          <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
            {/* Header — kept compact so the wait time is visible without scrolling */}
            <header className="text-center space-y-1 md:space-y-3 mt-2">
              <div className="flex items-center justify-center gap-3">
                <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                  {airport.name}
                </h1>
                <SaveAirportButton
                  airportCode={airport.code}
                  isSaved={isSaved(airport.code)}
                  onToggle={toggleSave}
                />
              </div>
              <p className="text-base md:text-xl text-muted-foreground">
                {airport.city} • Airport Code: {airport.code}
              </p>
            </header>

            {/* HERO: Wait Time Card */}
            <WaitTimeHero
              waitTime={waitTime}
              loading={loading}
              error={error}
              airportCode={airport.code}
            />

            {/* App install CTA — right after the leave-by answer, with live context */}
            <AppStoreCTA
              placement="airport_hero"
              context={airport.code}
              headline={
                waitTime
                  ? waitTime.standard.waitTime >= 20
                    ? `${airport.code} is running ${waitTime.standard.waitTime} minutes right now`
                    : `${airport.code} is moving quickly right now`
                  : `Know before you go at ${airport.code}`
              }
              subline={
                waitTime && waitTime.standard.waitTime >= 20
                  ? `Get pushed the moment the ${airport.code} line eases — plus a leave-by reminder and gate-change alerts. Free on iPhone.`
                  : `Get told the moment that changes, plus a leave-by reminder and gate-change alerts. Free on iPhone.`
              }
            />

            {/* The links worth competing with leaving */}
            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="py-6 space-y-4">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    Which {airport.code} terminal are you flying from?
                  </h2>
                  <p className="text-muted-foreground">
                    Terminals by airline, parking rates, checkpoints and transit for {airport.code}
                  </p>
                </div>
                {snippet?.tips && (
                  <ul className="space-y-2 text-base text-muted-foreground list-disc pl-5">
                    {snippet.tips.map((tip) => <li key={tip}>{tip}</li>)}
                  </ul>
                )}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  {hasTerminalData && (
                    <Button asChild variant="default" className="min-h-[44px] rounded-xl active:scale-95">
                      <Link
                        to={`/airports/${code?.toLowerCase()}/terminals`}
                        onClick={() => trackCtaClick("guide_next_step", airport.code)}
                      >
                        Terminals by airline
                      </Link>
                    </Button>
                  )}
                  {hasDiningData && (
                    <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95">
                      <Link
                        to={`/airports/${code?.toLowerCase()}/dining`}
                        onClick={() => trackCtaClick("guide_next_step", airport.code)}
                      >
                        Food & drink
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95">
                    <Link
                      to={`/airports/${code?.toLowerCase()}/parking`}
                      onClick={() => trackCtaClick("guide_next_step", airport.code)}
                    >
                      Parking rates
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95">
                    <Link
                      to={`/airports/${code?.toLowerCase()}/precheck`}
                      onClick={() => trackCtaClick("guide_next_step", airport.code)}
                    >
                      TSA PreCheck
                    </Link>
                  </Button>

                  <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95">
                    <Link
                      to={`/airports/${code?.toLowerCase()}/guide`}
                      onClick={() => trackCtaClick("guide_next_step", airport.code)}
                    >
                      Full airport guide
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Keep exploring — compact */}
            <QuickAirportRow currentCode={airport.code} />

            {/* Flight & Restaurant Previews (no sign-in gate) */}
            <div className="grid md:grid-cols-2 gap-6">
              <FlightPreview airportCode={airport.code} airportName={airport.name} />
              <RestaurantPreview airportCode={airport.code} airportName={airport.name} />
            </div>


            {/* Tabbed Content */}
            <AirportTabs airportCode={airport.code} airportName={airport.name} />

            {/* About Section (preserved for SEO) */}
            <Card>
              <CardContent className="pt-6 space-y-4">
                <h2 className="text-xl font-bold">About {airport.name}</h2>
                <p className="text-muted-foreground">
                  {airport.name} ({airport.code}) is a major airport serving {airport.city}.
                  Get real-time TSA security wait times, flight status updates, and restaurant information
                  to make your travel through {airport.code} as smooth as possible.
                </p>
                <div>
                  <h3 className="font-semibold mb-2">What You Can Track:</h3>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>Real-time TSA security checkpoint wait times</li>
                    <li>Live flight departure and arrival status</li>
                    <li>Restaurant locations and availability</li>
                    <li>Terminal information and navigation</li>
                  </ul>
                </div>
                <Button asChild size="lg" className="w-full">
                  <Link to="/auth">Get Started with Airport Wait Buddy</Link>
                </Button>
              </CardContent>
            </Card>

            {/* Best Times to Fly */}
            <BestTimesToFly airportCode={airport.code} airportName={airport.name} />

            {/* Crawlable content for SEO (visible to bots, not hidden in tabs) */}
            <CrawlableContent
              airportCode={airport.code}
              airportName={airport.name}
              city={airport.city}
            />

            {/* Blog cross-links for internal linking */}
            <BlogCrossLinks airportCode={airport.code} />

            {/* FAQ (SEO preserved) */}
            <AirportFAQ airportCode={airport.code} airportName={airport.name} city={airport.city} />

            {/* Free vs Pro */}
            <ProUpsellStrip />

            {/* Second app CTA near the end of the page */}
            <AppStoreCTA
              placement="airport_footer"
              context={airport.code}
              headline="Stop refreshing this page"
              subline={`Airport Buddy watches ${airport.code} for you and tells you when to leave. Free on iPhone.`}
            />

            {/* Email capture */}
            <EmailCapture source={`airport_${airport.code.toLowerCase()}`} />

            {/* Last Updated */}
            {waitTime && (
              <p className="text-sm text-muted-foreground text-center">
                <time dateTime={new Date(waitTime.lastUpdated).toISOString()}>
                  Updated {new Date(waitTime.lastUpdated).toLocaleString()}
                </time>
              </p>
            )}

            {/* Popular Airports */}
            <PopularAirports currentCode={airport.code} />
            <RelatedContent
              type="airport"
              currentId={airport.code}
              relatedKeywords={[airport.code, airport.city.split(",")[0], "airport", "TSA", "security"]}
              maxItems={4}
            />
          </div>
        </div>
      </div>

      {/* App install banner takes priority; signup banner stands down when it shows */}
      <AppInstallBanner airportCode={airport.code} onVisibilityChange={setInstallBannerVisible} />
      {!installBannerVisible && (
        <SmartSignupBanner airportCode={airport.code} airportName={airport.name} />
      )}
    </>
  );
};

export default AirportDetail;
